import { CheckIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  Content,
  Item,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { MergeExclusive } from 'type-fest';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import type { ButtonProps } from '../../../DesignSystem/Inputs/Button';
import { Button } from '../../../DesignSystem/Inputs/Button';
import { classNames } from '../../../DesignSystem/Utils/classNames';

export type PermissionOption = {
  id: string;
  label: string;
  subText?: string;
  displayLabel: string;
  isDelete?: boolean;
};

export type GroupedDropdownOptions = {
  id: string;
  options: PermissionOption[];
}[];

export function PermissionDropdown({
  selectedOption,
  children,
  groupedDropdownOptions,
  onSelected,
  triggerButtonLabel,
  triggerClassName,
  triggerButtonVariant = 'small',
  contentClassName,
}: MergeExclusive<
  {
    selectedOption: PermissionOption;
    groupedDropdownOptions: GroupedDropdownOptions;
    onSelected: (value: PermissionOption) => void;
    triggerClassName?: string;
    triggerButtonVariant?: ButtonProps['size'];
    contentClassName?: string;
  },
  {
    triggerButtonLabel: string;
    children: ReactNode;
  }
>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block w-full text-left">
      <Root open={isOpen} onOpenChange={setIsOpen}>
        <Trigger asChild>
          <Button
            className={twMerge(
              'w-full justify-between',
              isOpen && 'bg-gray-3 ring-2 ring-primary-6 ring-offset-2',
              triggerClassName
            )}
            size={triggerButtonVariant}
            variation="tertiaryLite"
            aria-label="permissionDropdown"
          >
            {selectedOption?.displayLabel}
            {triggerButtonLabel}
            <AnimatePresence initial={false}>
              <motion.div
                animate={isOpen ? 'opened' : 'closed'}
                variants={{
                  opened: { rotate: 0 },
                  closed: { rotate: -180 },
                }}
                transition={{ duration: 0.3 }}
              >
                <ChevronUpIcon className="h-4 w-4 text-gray-9" />
              </motion.div>
            </AnimatePresence>
          </Button>
        </Trigger>
        <Portal>
          <Content
            align="end"
            className={twMerge(
              'z-50 rounded-md border-gray-5 bg-gray-1  py-1 shadow-md-down',
              contentClassName
            )}
            sideOffset={5}
          >
            {children}
            {groupedDropdownOptions?.map((group, index) => (
              <>
                {group.options.map((option) => (
                  <Item
                    key={option.id}
                    onClick={() => {
                      onSelected(option);
                    }}
                    tabIndex={0}
                    className="cursor-pointer bg-gray-1 hover:bg-gray-3 focus:bg-gray-3 focus:outline-none"
                  >
                    <div className="inline-flex w-full items-center justify-start gap-2 px-3 py-2">
                      <div
                        className={classNames(
                          'shrink grow basis-0 text-sm leading-snug',
                          {
                            'font-bold': selectedOption.id === option.id,
                            'font-normal text-error-6': option.isDelete,
                            'text-gray-8': !option.isDelete,
                          }
                        )}
                      >
                        {option.label}
                        {option.subText && (
                          <TextStyle variant="xs-regular">
                            {option.subText}
                          </TextStyle>
                        )}
                      </div>
                      {selectedOption.id === option.id && (
                        <CheckIcon className="h-4 w-4 text-primary-5" />
                      )}
                    </div>
                  </Item>
                ))}
                {index !== groupedDropdownOptions.length - 1 && (
                  <div className="border-b border-solid border-b-gray-5" />
                )}
              </>
            ))}
          </Content>
        </Portal>
      </Root>
    </div>
  );
}
